import { useQuery } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { PaginatedResponse } from "modules/dashboard";
import {
    AdAccounts,
    AdAccountsRequest,
    CountyResponse,
    SettlementResponse,
    UserAccountsCountResponse,
    UserAccountsRequest,
    UserAccountsResponse,
    getAdUserAccounts,
    getAllCounties,
    getSettlementsForCounties,
    getSettlementsForCounty,
    getUserAccounts,
    getUserAccountsByUids,
    getUserAccountsCount,
    getUserFarmerIds,
} from "services";
import { useAccessToken } from "./useAccessToken";
import { useStandardErrorHandler } from "./useStandardErrorHandler";

export const useGetFilteredUserAccountsQuery = (data: UserAccountsRequest, enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju korisnika");

    return useQuery<AxiosResponse, Error, UserAccountsResponse>({
        queryKey: ["accounts", "filter", data],
        queryFn: () => getUserAccounts(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
        refetchOnMount: false,
    });
};

export const useGetUserAccountsCountQuery = (
    data: Partial<UserAccountsRequest>,
    enabled = true
) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju korisnika");

    return useQuery<AxiosResponse, Error, UserAccountsCountResponse>({
        queryKey: ["accounts", "count", data],
        queryFn: () => getUserAccountsCount(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data.count,
        refetchOnMount: false,
    });
};

export const useGetUserAccountsByUidQuery = (data: string[], enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju korisnika");

    return useQuery<AxiosResponse, Error, UserAccountsResponse>({
        queryKey: ["accounts", "uid", data],
        queryFn: () => getUserAccountsByUids(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useGetAllCounties = (enabled = true) => {
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju županija");

    return useQuery<AxiosResponse, Error, CountyResponse[]>({
        queryKey: ["counties"],
        queryFn: () => getAllCounties(),
        enabled,
        onError: errorHandler,
        select: response => response.data.counties,
    });
};

export const useGetSettlementsForCounty = (countyValue?: number, enabled = true) => {
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju naselja grada");

    return useQuery<AxiosResponse, Error, SettlementResponse[]>({
        queryKey: ["settlementsForCounty", countyValue],
        queryFn: () => getSettlementsForCounty(countyValue!),
        enabled: enabled && Boolean(countyValue),
        onError: errorHandler,
        select: response => response.data.settlements,
    });
};

export const useGetSettlementsForCounties = (countyValues: number[], enabled = true) => {
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju naselja grada");

    return useQuery<AxiosResponse, Error, SettlementResponse[]>({
        queryKey: ["settlementsForCounties", countyValues],
        queryFn: () => getSettlementsForCounties(countyValues),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data.settlements,
    });
};

export const useGetUserFarmerIds = (oib: string, enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju farmera");

    return useQuery<AxiosResponse, Error, number[]>({
        queryKey: ["userFarmerIds", oib],
        queryFn: () => getUserFarmerIds(oib, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data.farmerIds,
    });
};

export const useGetUserAccounts = (data: UserAccountsRequest, enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju korisnika");

    return useQuery<AxiosResponse, Error, UserAccountsResponse>({
        queryKey: ["getUserAccounts", data],
        queryFn: () => getUserAccounts(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};

export const useGetAdUserAccounts = (data: AdAccountsRequest, enabled = true) => {
    const accessToken = useAccessToken();
    const errorHandler = useStandardErrorHandler("Greška pri dohvaćanju korisnika");

    return useQuery<AxiosResponse, Error, PaginatedResponse<AdAccounts>>({
        queryKey: ["getAdUserAccounts", data],
        queryFn: () => getAdUserAccounts(data, accessToken!),
        enabled: enabled,
        onError: errorHandler,
        select: response => response.data,
    });
};
